/**
 * Machine skill labels — Soft brochure names for Live capability slugs only.
 * Never invent a claimed/verified state from these labels.
 * Edge catalog rows (skill_id) resolve via machine-edge-skills-catalog helpers.
 */
import { CAP_CATALOG } from "./edge-data.js";
import { isLocalHost } from "./pathseal.js";
import { operatorSay } from "./operator-say.js";

const BY_ID = new Map(CAP_CATALOG.map((c) => [c.id, c]));

export function skillSayOf(id) {
  const slug = String(id || "").trim();
  const row = BY_ID.get(slug);
  return row ? row.say : slug;
}

export function skillLabelOf(id) {
  const slug = String(id || "").trim();
  if (!slug) return "—";
  return operatorSay(
    slug,
    slug.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase()),
  );
}

export function skillClsOf(id) {
  const row = BY_ID.get(String(id || "").trim());
  return row ? row.cls : "live";
}

/**
 * Catalog rows for legacy cap-slug editor chips.
 * @param {string[]|object[]} liveIds capability slugs or edge catalog row objects
 * @param {{ allowSoftFallback?: boolean }} [opts]
 */
export function skillCatalogRowsOf(liveIds, opts) {
  const o = opts && typeof opts === "object" ? opts : {};
  if (Array.isArray(liveIds) && liveIds.length && typeof liveIds[0] === "object") {
    return liveIds.map((item) => ({
      id: String(item.id || item.skillId || "").trim(),
      label: String(item.label || item.id || "").trim(),
      say: String(item.say || "").trim(),
      cls: item.softPreview ? "soft" : "live",
      risk: "",
      caps: item.requiredCapabilities || item.caps || [],
      catalog: true,
    })).filter((r) => r.id);
  }
  const ids = Array.isArray(liveIds) && liveIds.length
    ? liveIds.map((c) => String(c).trim()).filter(Boolean)
    : (o.allowSoftFallback === true && isLocalHost()
      ? CAP_CATALOG.map((c) => c.id)
      : []);
  return ids.map((id) => {
    const row = BY_ID.get(id);
    return row || { id, say: id, cls: "live", risk: "" };
  });
}

/**
 * Build skill → deviceId[] index from GET /devices/capabilities items
 * or from a map of deviceId → { claimed[] }.
 */
export function skillIndexOf(itemsOrMap) {
  const bySkill = new Map();
  const byDevice = new Map();

  const add = (deviceId, claimed) => {
    const did = String(deviceId || "").trim();
    if (!did) return;
    const caps = (Array.isArray(claimed) ? claimed : [])
      .map((c) => String(c).trim()).filter(Boolean);
    byDevice.set(did, caps);
    for (const slug of caps) {
      if (!bySkill.has(slug)) bySkill.set(slug, []);
      bySkill.get(slug).push(did);
    }
  };

  if (itemsOrMap instanceof Map) {
    for (const [did, row] of itemsOrMap) {
      add(did, row && row.claimed);
    }
  } else if (Array.isArray(itemsOrMap)) {
    for (const row of itemsOrMap) {
      if (!row || typeof row !== "object") continue;
      add(row.device_id || row.deviceId || row.id, row.claimed);
    }
  }

  const tabs = [...bySkill.keys()].sort((a, b) => a.localeCompare(b)).map((id) => ({
    id,
    label: skillLabelOf(id),
    say: skillSayOf(id),
    count: (bySkill.get(id) || []).length,
  }));

  return { bySkill, byDevice, tabs };
}
